import { Injectable } from "@angular/core";
import { DialogService, DialogRef, DialogCloseResult } from "@progress/kendo-angular-dialog";
import { CustomModule } from "src/app/shared/custom.module";
import { ModalComponent } from "src/app/shared/components/modal/modal.component";
import { ModalDataService } from "src/app/shared/components/modal/modal-data.service";

import * as $ from "jquery";

/**
 * [ TS ]
 * 1. custom.module.ts에 Dialog 모듈 import
 *     - import { DialogModule } from '@progress/kendo-angular-dialog';
 *     - export: [ ..., DialogModule, ... ]
 *
 * [ HTML ]
 * 1. app.component.html에 <div kendoDialogContainer></div>태그를 추가
 *
 * [ CSS ]
 * 1. content에 \n를 먹이기 위해 k-dialog-content에 white-space: pre-wrap; 스타일을 추가해야 함
 *     - .k-dialog-content { white-space: pre-wrap; }
 * 2. 우측 상단의 close 버튼을 지우기 위해서는 k-window-actions에 display: none; 스타일 추가해야 함
 *     - .k-window-actions { display: none; }
 */
@Injectable({
  providedIn: CustomModule
})
export class ModalService {
  constructor(private dialogService: DialogService, private modalData: ModalDataService) { }

  /**
   * 단순 내용 modal
   * @param content (string) 내용에 표시될 Text
   * @param btnText (string) 버튼에 표시될 Text
   * @param modalClass (array) Modal Component의 최상위 Element에 추가할 Class
   * @param height (number) Modal의 높이로 Kendo ui에서 설정된 기본 파라미터
   * @param width (number) Modal의 넓이로 Kendo ui에서 설정된 기본 파라미터
   * @param minWidth (number) Modal의 최소 넓이로 Kendo ui에서 설정된 기본 파라미터
   */
  notice({ content = "", btnText = "OK", modalClass = ['pop_alert'], height = 0, width = 0, minWidth = 0 }) {
    let contentComponent: any;

    // 전달받은 content 영역이 String이 아닐경우 content에 바로 대입
    if (typeof content === "string") {
      // Base Component에 메시지 전달 (\n의 값은 <br/>로 변경하여 줄내림 구현)
      this.modalData.sendMessage(content.replace(/\n/gi, "<br/>"));
      contentComponent = ModalComponent;
    } else {
      contentComponent = content;
    }

    // Text에 Html로 작성한 것도 반영하기 위해 Base Component로 Open
    this.dialogService.open({
      content: ModalComponent,
      actions: [
        {
          text: btnText
        }
      ],
      height,
      width,
      minWidth
    });

    $("kendo-dialog").last().addClass(modalClass);
  }

  /**
   * 버튼이 1개인 modal
   * @param title (string) modal 타이틀에 표시될 Text
   * @param content (string) 내용에 표시될 Text
   * @param btnText (string) 버튼에 표시될 Text
   * @param modalClass (array) Modal Component의 최상위 Element에 추가할 Class
   * @param callback (function) 버튼 클릭시 응답받을 함수
   * @param height (number) Modal의 높이로 Kendo ui에서 설정된 기본 파라미터
   * @param width (number) Modal의 넓이로 Kendo ui에서 설정된 기본 파라미터
   * @param minWidth (number) Modal의 최소 넓이로 Kendo ui에서 설정된 기본 파라미터
   */
  alert({
    title = "",
    content,
    btnText = "OK",
    modalClass = ["pop_alert"],
    callback = (res: any) => { },
    height = 0,
    width = 700,
    minWidth = 320
  }) {
    let contentComponent: any;

    // 전달받은 content 영역이 String이 아닐경우 content에 바로 대입
    if (typeof content === "string") {
      // Base Component에 메시지 전달 (\n의 값은 <br/>로 변경하여 줄내림 구현)
      this.modalData.sendMessage(content.replace(/\n/gi, "<br/>"));
      contentComponent = ModalComponent;
    } else {
      contentComponent = content;
    }

    // Text에 Html로 작성한 것도 반영하기 위해 Base Component로 Open
    const dialog: DialogRef = this.dialogService.open({
      title,
      content: contentComponent,
      actions: [
        {
          text: btnText
        }
      ],
      height,
      width,
      minWidth
    });

    /**
     * Modal의 가장 상위에 추가할 클래스
     */
    $('kendo-dialog').last().addClass(modalClass);
    // $('kendo-dialog').addClass(modalClass);

    dialog.result.subscribe((res) => {
      if (res instanceof DialogCloseResult) {
        callback(false);
      } else {
        callback(res);
      }
    });
  }

  /**
   * Confirm 창
   * @param title (string) modal 타이틀에 표시될 Text
   * @param content (string) 내용에 표시될 Text
   * @param lBtn (object) 좌측 버튼과 관련된 Object
   * @param lBtn.btnText (string) 좌측 버튼에 표시될 Text
   * @param lBtn.callback (function) 좌측 버튼 클릭시 응답받을 함수
   * @param rBtn (object) 우측 버튼과 관련된 Object
   * @param rBtn.btnText (string) 우측 버튼에 표시될 Text
   * @param rBtn.callback (function) 우측 버튼 클릭시 응답받을 함수
   * @param modalClass (array) Modal Component의 최상위 Element에 추가할 Class
   * @param callback (function) modal이 close될시 응답받을 함수 (btn의 callback 실행 후 실행)
   * @param height (number) Modal의 높이로 Kendo ui에서 설정된 기본 파라미터
   * @param width (number) Modal의 넓이로 Kendo ui에서 설정된 기본 파라미터
   * @param minWidth (number) Modal의 최소 넓이로 Kendo ui에서 설정된 기본 파라미터
   */
  confirm({
    title = "",
    content,
    lBtn = {},
    rBtn = {},
    modalClass = [],
    callback = (res: any) => { },
    height = 0,
    width = 0,
    minWidth = 0
  }) {
    // tslint:disable-next-line: no-string-literal
    const lBtnText = lBtn["btnText"] || "Close";
    // tslint:disable-next-line: no-string-literal
    const lBtnCallback = lBtn["callback"] || (() => { });

    // tslint:disable-next-line: no-string-literal
    const rBtnText = rBtn["btnText"] || "Confirm";
    // tslint:disable-next-line: no-string-literal
    const rBtnCallback = rBtn["callback"] || (() => { });

    let contentComponent: any;

    // 전달받은 content 영역이 String이 아닐경우 content에 바로 대입
    if (typeof content === "string") {
      // Base Component에 메시지 전달 (\n의 값은 <br/>로 변경하여 줄내림 구현)
      this.modalData.sendMessage(content.replace(/\n/gi, "<br/>"));
      contentComponent = ModalComponent;
    } else {
      contentComponent = content;
    }

    // Text에 Html로 작성한 것도 반영하기 위해 Base Component로 Open
    const dialog: DialogRef = this.dialogService.open({
      title,
      content: contentComponent,
      actions: [
        {
          text: lBtnText
        },
        {
          text: rBtnText
        }
      ],
      height,
      width,
      minWidth
    });

    $("kendo-dialog").last().addClass(modalClass);
    dialog.result.subscribe((res) => {
      if (res instanceof DialogCloseResult) {
        callback(false);
      } else {
        switch (res.text) {
          case lBtnText:
            lBtnCallback(res);
            callback(res);
            break;

          case rBtnText:
            rBtnCallback(res);
            callback(res);
            break;
        }
      }
    });
  }

  /**
   * 화면 Open 함수 (호출된 component에서 @Input modal: any; 선언하여 접근)
   * @param content (component) 화면에 표시될 component
   * @param message (object) 화면에 전달할 object
   * @param opener (object) 오픈된 화면에서 Parent에서 데이터 접근용 object
   * @param callback (function) modal이 close될시 응답받을 함수 (btn의 callback 실행 후 실행)
   * @param callback (function) 화면을 닫을때 응답 받을 함수
   */
  open({ content, message = {}, opener = {}, modalClass = [], callback = (res: any) => { } }) {
    const dialog: DialogRef = this.dialogService.open({
      content
    });

    $("kendo-dialog").last().addClass(modalClass);
    dialog.content.instance.modal = {
      message,
      callback,
      opener,
      close: (res: any) => {
        dialog.close(res);
      }
    };

    dialog.result.subscribe((res) => {
      if (res instanceof DialogCloseResult) {
        callback(false);
      } else {
        callback(res);
      }
    });
  }
}
